import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { navLinks } from '@/mocks/homeData';
import ScrollProgress from '@/components/feature/ScrollProgress';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <ScrollProgress />
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className={`fixed top-[2px] left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.03)]'
            : 'bg-transparent'
        }`}
      >
        <nav className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="relative z-10 group flex items-center gap-2.5">
              {/* Clinic logo image */}
              <span className="w-10 h-10 flex items-center justify-center overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_062c36e0a47ea70a58d13149f7f74287_0b091c46.jpg"
                  alt="Femme Vie Logo"
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </span>
              <span className={`font-heading text-2xl md:text-3xl tracking-[0.15em] transition-all duration-500 ${
                scrolled ? 'text-foreground-950' : 'text-white'
              }`}>
                Femme<span className="font-light italic">Vie</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`relative text-[13px] tracking-[0.08em] uppercase transition-colors duration-300 whitespace-nowrap ${
                      scrolled
                        ? isActive ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
                        : isActive ? 'text-white' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-[1.5px] bg-primary-500"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
              <a
                href={`https://wa.me/${'5585984293412'}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-5 py-2.5 rounded-full text-[13px] tracking-[0.06em] uppercase whitespace-nowrap transition-all duration-300 border ${
                  scrolled
                    ? 'border-primary-400 text-primary-600 hover:bg-primary-500 hover:text-white'
                    : 'border-white/40 text-white hover:bg-white hover:text-primary-600'
                }`}
              >
                Agendar Consulta
              </a>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden relative z-10 w-10 h-10 flex items-center justify-center"
              aria-label="Menu"
            >
              <div className="flex flex-col gap-[5px]">
                <span className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  scrolled ? 'bg-foreground-950' : 'bg-white'
                } ${mobileOpen ? 'rotate-45 translate-y-[3.5px] bg-foreground-950' : ''}`} />
                <span className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  scrolled ? 'bg-foreground-950' : 'bg-white'
                } ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  scrolled ? 'bg-foreground-950' : 'bg-white'
                } ${mobileOpen ? '-rotate-45 -translate-y-[3.5px] bg-foreground-950' : ''}`} />
              </div>
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background-50 flex flex-col items-center justify-center gap-8 lg:hidden"
          >
            {navLinks.map((link, i) => {
              const isActive = location.pathname === link.href;
              return (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={link.href}
                    className={`text-2xl font-heading tracking-[0.08em] transition-colors duration-300 whitespace-nowrap ${
                      isActive ? 'text-primary-500' : 'text-foreground-800 hover:text-primary-500'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              );
            })}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ delay: navLinks.length * 0.06, duration: 0.4 }}
            >
              <a
                href={`https://wa.me/${'5585984293412'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 px-8 py-3 rounded-full text-sm tracking-[0.08em] uppercase whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300"
              >
                Agendar Consulta
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}